import { useEffect, useRef, useState } from 'react';

type Props = {
  onCaptured: (file: File) => void;
  onCancel: () => void;
  disabled?: boolean;
  // Lets the parent (Conversation composer) know when we're actively
  // recording, so it can hand the whole compose bar over to the recorder
  // UI and hide the textarea + other buttons.
  onRecordingChange?: (recording: boolean) => void;
};

type AudioDevice = { deviceId: string; label: string };

/**
 * Records a single voice note via MediaRecorder. Shows the active mic
 * label + a real-time waveform of the audio level so the user can see
 * their voice is actually being picked up. Falls back to a device
 * picker if the default isn't the right input.
 */
export function VoiceRecorder({ onCaptured, onCancel, disabled, onRecordingChange }: Props) {
  const [phase, setPhase] = useState<'idle' | 'recording'>('idle');
  const [elapsedMs, setElapsedMs] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [deviceLabel, setDeviceLabel] = useState<string>('');
  const [devices, setDevices] = useState<AudioDevice[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | undefined>(undefined);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Recent audio levels — a rolling buffer that drives the waveform.
  const [levels, setLevels] = useState<number[]>(() => new Array(24).fill(0));

  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startTimeRef = useRef<number>(0);
  const tickRef = useRef<number | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const rafRef = useRef<number | null>(null);
  const levelHistoryRef = useRef<number[]>(new Array(24).fill(0));

  useEffect(() => {
    return () => {
      stopAll();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tell the parent composer whenever we enter/leave the recording state so
  // it can swap the whole compose bar over to the recorder UI.
  useEffect(() => {
    onRecordingChange?.(phase === 'recording');
  }, [phase, onRecordingChange]);

  function stopAll() {
    if (recorderRef.current && recorderRef.current.state === 'recording') {
      try {
        recorderRef.current.stop();
      } catch {
        // ignore
      }
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (tickRef.current !== null) {
      window.clearInterval(tickRef.current);
      tickRef.current = null;
    }
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (audioCtxRef.current) {
      void audioCtxRef.current.close().catch(() => undefined);
      audioCtxRef.current = null;
    }
  }

  function diagnoseMicError(err: unknown): string {
    if (!(err instanceof Error)) return 'Microphone unavailable.';
    const name = (err as { name?: string }).name ?? '';
    const msg = err.message || '';
    if (name === 'NotAllowedError') {
      return 'Mic blocked. Click the padlock left of the URL → allow Microphone → reload. If the padlock shows it as already allowed, your OS may be blocking the browser (macOS: System Settings → Privacy → Microphone).';
    }
    if (name === 'NotFoundError' || name === 'OverconstrainedError') {
      return 'No microphone found on this device.';
    }
    if (name === 'NotReadableError') {
      return 'Microphone is busy — another app may be using it.';
    }
    return `Microphone unavailable: ${msg || name || 'unknown error'}`;
  }

  function pickMimeType(): string {
    const candidates = [
      'audio/webm;codecs=opus',
      'audio/ogg;codecs=opus',
      'audio/mp4;codecs=mp4a',
      'audio/mp4',
      'audio/webm',
    ];
    for (const c of candidates) {
      if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(c)) {
        return c;
      }
    }
    return '';
  }

  async function listDevices() {
    try {
      const all = await navigator.mediaDevices.enumerateDevices();
      const audio = all
        .filter((d) => d.kind === 'audioinput')
        .map((d, i) => ({
          deviceId: d.deviceId,
          label: d.label || `Microphone ${i + 1}`,
        }));
      setDevices(audio);
    } catch {
      // ignore
    }
  }

  async function start() {
    setError(null);
    try {
      const constraints: MediaStreamConstraints = {
        audio: selectedDeviceId
          ? { deviceId: { exact: selectedDeviceId } }
          : {
              // Default device — but tell the browser we want a real mic, not
              // a system loopback. echoCancellation + noiseSuppression bias
              // toward voice inputs; many virtual/loopback devices don't
              // support these and the browser will skip them.
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
            },
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      // Once we have permission, labels become populated. List devices so
      // the user can switch if the wrong one was picked.
      const track = stream.getAudioTracks()[0];
      setDeviceLabel(track.label || 'Default microphone');
      void listDevices();

      const mimeType = pickMimeType();
      // 96 kbps Opus VBR is roughly studio-quality voice. The MediaRecorder
      // default audio bitrate on mobile browsers can be as low as ~20 kbps,
      // which causes the "voice in a tin can" artifacts our testers heard.
      const recOptions: MediaRecorderOptions = { audioBitsPerSecond: 96000 };
      if (mimeType) recOptions.mimeType = mimeType;
      const rec = new MediaRecorder(stream, recOptions);
      recorderRef.current = rec;
      chunksRef.current = [];

      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = () => {
        const type = rec.mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type });
        const ext = type.includes('mp4') ? 'm4a' : type.includes('ogg') ? 'ogg' : 'webm';
        const file = new File([blob], `voice-${Date.now()}.${ext}`, { type });
        stopAll();
        setPhase('idle');
        setElapsedMs(0);
        levelHistoryRef.current = new Array(24).fill(0);
        setLevels(levelHistoryRef.current);
        onCaptured(file);
      };

      rec.start(250);
      startTimeRef.current = Date.now();
      setPhase('recording');

      tickRef.current = window.setInterval(() => {
        setElapsedMs(Date.now() - startTimeRef.current);
      }, 100);

      const ctx = new AudioContext();
      audioCtxRef.current = ctx;
      if (ctx.state === 'suspended') {
        await ctx.resume().catch(() => undefined);
      }
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.4;
      source.connect(analyser);
      analyserRef.current = analyser;
      const data = new Uint8Array(analyser.frequencyBinCount);
      let frame = 0;
      const updateLevel = () => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteFrequencyData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) sum += data[i];
        const avg = sum / data.length;
        const lvl = Math.min(1, avg / 72);
        // Shift the rolling buffer once every ~3 RAF frames (~50 ms at
        // 60fps). Gives a visible waveform without churn.
        frame += 1;
        if (frame % 3 === 0) {
          const next = [...levelHistoryRef.current.slice(1), lvl];
          levelHistoryRef.current = next;
          setLevels(next);
        }
        rafRef.current = requestAnimationFrame(updateLevel);
      };
      updateLevel();
    } catch (err) {
      setError(diagnoseMicError(err));
      setPhase('idle');
    }
  }

  function stop() {
    if (recorderRef.current && recorderRef.current.state === 'recording') {
      recorderRef.current.stop();
    }
  }

  function cancel() {
    stopAll();
    setPhase('idle');
    setElapsedMs(0);
    levelHistoryRef.current = new Array(24).fill(0);
    setLevels(levelHistoryRef.current);
    onCancel();
  }

  async function switchDevice(id: string) {
    setSelectedDeviceId(id);
    setPickerOpen(false);
    // If currently recording, restart with the new device.
    if (phase === 'recording') {
      cancel();
      // small delay so cleanup completes
      window.setTimeout(() => void start(), 50);
    }
  }

  const seconds = Math.floor(elapsedMs / 1000);
  const mm = Math.floor(seconds / 60);
  const ss = (seconds % 60).toString().padStart(2, '0');

  if (phase === 'idle' && !error) {
    return (
      <button
        type="button"
        className="btn btn-ghost composer-attach-btn"
        onClick={start}
        disabled={disabled}
        aria-label="Record voice note"
        title="Record voice note"
      >
        🎙️
      </button>
    );
  }

  return (
    <div className="voice-recorder">
      {error ? (
        <>
          <span className="error">{error}</span>
          <button type="button" className="btn btn-ghost btn-small" onClick={cancel}>
            Close
          </button>
        </>
      ) : (
        <>
          <span className="voice-recorder-dot" aria-hidden="true" />
          <span className="voice-recorder-timer">
            {mm}:{ss}
          </span>
          <span className="voice-recorder-wave" aria-hidden="true">
            {levels.map((l, i) => (
              <span
                key={i}
                className="voice-recorder-wave-bar"
                style={{ height: `${Math.max(8, l * 100)}%` }}
              />
            ))}
          </span>
          {devices.length > 1 ? (
            <div className="voice-recorder-device">
              <button
                type="button"
                className="voice-recorder-device-trigger"
                onClick={() => setPickerOpen((v) => !v)}
                title={deviceLabel}
              >
                {truncateLabel(deviceLabel)} ▾
              </button>
              {pickerOpen && (
                <div className="voice-recorder-device-menu" role="menu">
                  {devices.map((d) => (
                    <button
                      key={d.deviceId}
                      type="button"
                      className={`voice-recorder-device-item ${
                        d.label === deviceLabel ? 'on' : ''
                      }`}
                      onClick={() => void switchDevice(d.deviceId)}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <span className="voice-recorder-device-static" title={deviceLabel}>
              {truncateLabel(deviceLabel)}
            </span>
          )}
          <button type="button" className="btn btn-ghost btn-small" onClick={cancel}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary btn-small" onClick={stop}>
            Stop
          </button>
        </>
      )}
    </div>
  );
}

function truncateLabel(s: string): string {
  if (!s) return '';
  // Browser device labels can be long ("Default - MacBook Pro Microphone (Built-in)").
  // Trim aggressively for the pill.
  const cleaned = s.replace(/^Default\s*-\s*/i, '').replace(/\s*\(.*\)\s*$/, '');
  return cleaned.length > 22 ? cleaned.slice(0, 22) + '…' : cleaned;
}
